import React from 'react';
import { ArrowLeft, Shield, AlertTriangle, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { legalBrand } from './legalBrand';

const AIUsagePolicy = ({ brand }) => {
  const b = legalBrand(brand);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to={b.home} 
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {b.homeLabel}
          </Link>
          <h1 className="text-4xl font-light text-gray-900 mb-2">AI Usage & Content Protection Policy</h1>
          <p className="text-gray-600">Effective Date: January 1, 1988</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 space-y-8">
          
          {/* Alert Banner */}
          <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertTriangle className="text-red-600 mr-3 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">Strict Prohibition Notice</h3>
                <p className="text-red-800 leading-relaxed">
                  All content on this website is strictly protected from artificial intelligence and machine learning use. 
                  Violations will be prosecuted to the fullest extent of the law.
                </p>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Purpose and Scope</h2>
            <p className="text-gray-700 leading-relaxed">
              This AI Usage & Content Protection Policy ("Policy") establishes comprehensive restrictions on the use of content 
              from this website and all associated platforms for artificial intelligence, machine learning, and automated data 
              processing purposes. This Policy is an integral part of our Terms of Service and is governed by the laws of the Universe.
            </p>
          </section>

          {/* Absolute Prohibition */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Absolute Prohibition on AI/ML Use</h2>
            
            <div className="bg-purple-50 border-2 border-purple-600 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <Shield className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-3">Protected Content</h3>
                  <p className="text-gray-800 leading-relaxed mb-3">
                    <strong>Copyright © 1988 CT / KEEPER OF THE FIYAH. All Rights Reserved.</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Unauthorized reproduction, distribution, or adaptation of any material is strictly prohibited. This includes, 
                    but is not limited to, all text, images, graphics, logos, digital assets, research materials, and any other 
                    content on this website or associated platforms.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.1 Prohibited AI Systems</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              No content from this website may be used in connection with any of the following, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Large Language Models (LLMs)</strong> - Including GPT, Claude, Gemini, LLaMA, and any similar systems</li>
              <li><strong>Generative AI</strong> - Text generation, image generation, video generation, audio generation</li>
              <li><strong>Neural Networks</strong> - Deep learning models, artificial neural networks, convolutional networks</li>
              <li><strong>Machine Learning Models</strong> - Supervised learning, unsupervised learning, reinforcement learning</li>
              <li><strong>Natural Language Processing (NLP)</strong> - Text analysis, sentiment analysis, language understanding</li>
              <li><strong>Computer Vision Systems</strong> - Image recognition, object detection, facial recognition</li>
              <li><strong>Automated Data Collection</strong> - Web scrapers, crawlers, bots, spiders</li>
              <li><strong>Training Datasets</strong> - Any dataset compilation for AI/ML training purposes</li>
              <li><strong>Fine-tuning Operations</strong> - Adaptation of existing AI models using our content</li>
              <li><strong>Prompt Engineering</strong> - Using our content to generate AI prompts or responses</li>
              <li><strong>Knowledge Graphs</strong> - Semantic networks or knowledge bases incorporating our content</li>
              <li><strong>Embedding Models</strong> - Vector representations or semantic embeddings of our content</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.2 Prohibited Activities</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following activities are expressly forbidden:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Copying, scraping, indexing, downloading, or ingesting content for any AI/ML purpose</li>
              <li>Training, developing, or refining artificial intelligence systems using our content</li>
              <li>Creating derivative works through AI prompts, algorithms, or automated tools</li>
              <li>Using content to generate synthetic or artificial outputs</li>
              <li>Incorporating content into any automated decision-making system</li>
              <li>Mining or extracting patterns, insights, or knowledge from our content</li>
              <li>Creating embeddings, vectors, or other mathematical representations</li>
              <li>Using content for model evaluation, testing, or benchmarking</li>
              <li>Caching, mirroring, or storing content for AI/ML access</li>
            </ul>
          </section>

          {/* Technical Measures */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Technical Protection Measures</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement multiple layers of protection to prevent unauthorized AI/ML access:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>robots.txt Directives:</strong> Explicit disallow rules for AI crawlers and bots</li>
              <li><strong>User-Agent Blocking:</strong> Detection and blocking of known AI/ML user agents</li>
              <li><strong>Rate Limiting:</strong> Prevention of automated bulk content access</li>
              <li><strong>Access Monitoring:</strong> Continuous surveillance for suspicious access patterns</li>
              <li><strong>Digital Watermarking:</strong> Tracking of content usage and unauthorized distribution</li>
              <li><strong>CAPTCHA Implementation:</strong> Verification of human users</li>
              <li><strong>API Restrictions:</strong> No API access for AI/ML purposes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Circumvention of any of these technical measures constitutes a violation of the Digital Millennium Copyright Act (DMCA) 
              and other applicable laws.
            </p>
          </section>

          {/* Legal Enforcement */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Enforcement and Legal Remedies</h2>
            
            <div className="bg-gray-100 border-l-4 border-gray-700 p-6 rounded-r-lg mb-6">
              <div className="flex items-start">
                <Scale className="text-gray-700 mr-3 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Legal Action</h3>
                  <p className="text-gray-800 leading-relaxed">
                    Violations of this Policy will be prosecuted to the fullest extent of the law, including pursuit of injunctive 
                    relief and monetary damages under the Digital Millennium Copyright Act (DMCA) and applicable international treaties.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Civil Remedies</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Violators may be subject to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Injunctive Relief:</strong> Court orders to immediately cease all unauthorized use</li>
              <li><strong>Statutory Damages:</strong> Up to $150,000 per work infringed under 17 U.S.C. § 504(c)</li>
              <li><strong>Actual Damages:</strong> Compensation for all provable financial losses</li>
              <li><strong>Disgorgement of Profits:</strong> Recovery of any profits derived from unauthorized use</li>
              <li><strong>Attorneys' Fees:</strong> Recovery of all legal costs and expenses</li>
              <li><strong>Court Costs:</strong> All expenses associated with litigation</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">4.2 Criminal Penalties</h3>
            <p className="text-gray-700 leading-relaxed">
              Under 17 U.S.C. § 506 and 18 U.S.C. § 2319, willful copyright infringement for commercial advantage or private 
              financial gain may result in criminal prosecution, including imprisonment and substantial fines.
            </p>
          </section>

          {/* Monitoring and Detection */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Monitoring and Detection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We actively monitor for unauthorized use of our content, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Regular audits of AI/ML training datasets</li>
              <li>Monitoring of AI model outputs for our content</li>
              <li>Collaboration with copyright enforcement agencies</li>
              <li>Use of digital forensics and content recognition technology</li>
              <li>Investigation of suspicious access patterns and bulk downloads</li>
              <li>Engagement with AI companies to ensure compliance</li>
            </ul>
          </section>

          {/* Licensing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Licensing and Permissions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Organizations seeking to use our content for AI/ML purposes must:
            </p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li>Submit a formal written request to: {b.privacy}</li>
              <li>Provide detailed information about the intended use, including:
                <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                  <li>Specific AI/ML system or application</li>
                  <li>Scope and nature of content to be used</li>
                  <li>Commercial or non-commercial purpose</li>
                  <li>Duration of use</li>
                  <li>Geographic distribution</li>
                </ul>
              </li>
              <li>Negotiate licensing terms and compensation</li>
              <li>Execute a formal licensing agreement</li>
              <li>Comply with all terms and conditions of the license</li>
            </ol>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Important:</strong> Failure to obtain explicit written permission before any AI/ML use constitutes 
              copyright infringement and will be prosecuted accordingly.
            </p>
          </section>

          {/* International Protection */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Protection</h2>
            <p className="text-gray-700 leading-relaxed">
              This Policy is enforceable worldwide under international copyright treaties and conventions, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Berne Convention for the Protection of Literary and Artistic Works</li>
              <li>World Intellectual Property Organization (WIPO) Copyright Treaty</li>
              <li>Agreement on Trade-Related Aspects of Intellectual Property Rights (TRIPS)</li>
              <li>Universal Copyright Convention (UCC)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This Policy is governed by the laws of the Universe, without regard to conflict of law provisions.
            </p>
          </section>

          {/* Reporting Violations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Reporting Violations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you become aware of any violation of this Policy, including unauthorized AI/ML use of our content, 
              please report it immediately to:
            </p>
            <div className="mt-4 p-6 bg-gray-50 rounded-lg border-2 border-gray-300">
              <p className="text-gray-800 font-medium mb-2">Copyright Enforcement</p>
              <p className="text-gray-700">Email: {b.privacy}</p>
              <p className="text-gray-700 mt-3 text-sm">Please include:</p>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4 mt-2">
                <li>Description of the violation</li>
                <li>Location or source of unauthorized use</li>
                <li>Evidence or documentation</li>
                <li>Your contact information</li>
              </ul>
            </div>
          </section>

          {/* Updates to Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to update this Policy at any time to address new AI/ML technologies and threats. Updates 
              will be posted on this page with a revised effective date. Continued access to or use of this website after any 
              changes constitutes acceptance of the updated Policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions, licensing inquiries, or to report violations:
            </p>
            <div className="mt-4 p-6 bg-purple-50 rounded-lg border-2 border-purple-200">
              <p className="text-gray-800 font-medium">CT / KEEPER OF THE FIYAH</p>
              <p className="text-gray-700">Email: {b.privacy}</p>
              <p className="text-gray-700 mt-2 text-sm italic">All inquiries will be responded to within 5-7 business days.</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default AIUsagePolicy;